import { z } from 'zod';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { API_ROUTES, QUERY_KEYS } from '@/config/constants';
import { toaster } from '@/components/ui/lib/toast-utils';
import { DICTIONARY } from '@/config/dictionary';
import { getApiErrorMessage } from '@/utils/api-error';
import { useInvalidateNotifications } from '@/hooks/use-invalidate-notifications';
import type {
  AdminCountryConfigMap,
  AdminServicesConfig,
} from '@/features/admin-settings/types/admin-config';
import type {
  AdminPricingConfig,
  UpdateAdminPricingPayload,
} from '@/features/admin-settings/types/admin-pricing';
import type {
  AdminCtsFormConfig,
  CtsDocument,
} from '@/features/admin-settings/types/admin-cts-form';
import withoutCatalogItem from '@/features/admin-settings/utils/without-catalog-item';
import type { EntityType } from '@/features/admin-settings/types/catalog-visibility';
import type { ServiceFeatureCatalog } from '@/types/service-feature-catalog';

// ─── Zod schemas for mutation inputs ──────────────────────────────────────────

export const UpdateAdminCountryConfigSchema = z.object({
  countries: z.array(
    z.object({
      countryCode: z.string(),
      byoc: z.boolean(),
      onDemand: z.boolean(),
      onRequest: z.boolean(),
    }),
  ),
});
export type UpdateAdminCountryConfigPayload = z.infer<typeof UpdateAdminCountryConfigSchema>;

export const UpdateAdminServicesConfigSchema = z.object({
  serviceConfigs: z.array(
    z.object({
      serviceId: z.string(),
      visibility: z.enum(['off', 'disabled', 'available', 'on']),
    }),
  ),
  featureConfigs: z.array(
    z.object({
      featureId: z.string(),
      visibility: z.enum(['off', 'disabled', 'available', 'on']),
    }),
  ),
});
export type UpdateAdminServicesConfigPayload = z.infer<typeof UpdateAdminServicesConfigSchema>;

export const UpdateAdminPricingSchema = z.object({
  freeCreditAmountUsd: z.number().min(0),
  currency: z.string().min(1),
  minTopUpUsd: z.number().min(0),
  maxTopUpUsd: z.number().min(0),
  // Only sent once the backend returns it — see AdminPricingConfig.suspensionLevelUsd.
  suspensionLevelUsd: z.number().min(0).optional(),
  platformPricing: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      description: z.string(),
      pricePerMonth: z.number().min(0),
    }),
  ),
  featurePricing: z.array(
    z.object({
      featureId: z.string(),
      name: z.string(),
      description: z.string(),
      pricePerMonth: z.number().min(0),
    }),
  ),
  servicePricing: z.array(
    z.object({
      serviceId: z.string(),
      name: z.string(),
      description: z.string(),
      pricePerMonth: z.number().min(0),
    }),
  ),
});

export const UpdateCtsFormCountrySchema = z.object({
  documents: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      description: z.string(),
      orderType: z.enum(['New', 'Port']),
      priority: z.number().int().min(0),
    }),
  ),
});
export type UpdateCtsFormCountryPayload = z.infer<typeof UpdateCtsFormCountrySchema>;

// ─── Fetchers ─────────────────────────────────────────────────────────────────

const fetchAdminCountryConfig = async (): Promise<AdminCountryConfigMap> => {
  const { data } = await api.get<AdminCountryConfigMap>(API_ROUTES.admin.countryConfig);
  return data;
};

const updateAdminCountryConfig = async (
  payload: UpdateAdminCountryConfigPayload,
): Promise<AdminCountryConfigMap> => {
  const { data } = await api.put<AdminCountryConfigMap>(API_ROUTES.admin.countryConfig, payload);
  return data;
};

const fetchAdminServicesConfig = async (): Promise<AdminServicesConfig> => {
  const { data } = await api.get<AdminServicesConfig>(API_ROUTES.admin.servicesConfig);
  return data;
};

const updateAdminServicesConfig = async (
  payload: UpdateAdminServicesConfigPayload,
): Promise<AdminServicesConfig> => {
  const { data } = await api.put<AdminServicesConfig>(API_ROUTES.admin.servicesConfig, payload);
  return data;
};

const deleteCatalogItem = async (entity: EntityType, id: string): Promise<void> => {
  const url =
    entity === 'service' ? API_ROUTES.catalog.service(id) : API_ROUTES.catalog.feature(id);
  await api.delete(url);
};

const fetchAdminPricingConfig = async (): Promise<AdminPricingConfig> => {
  const { data } = await api.get<AdminPricingConfig>(API_ROUTES.admin.pricingConfig);
  return data;
};

const updateAdminPricingConfig = async (
  payload: UpdateAdminPricingPayload,
): Promise<AdminPricingConfig> => {
  const validatedPayload = UpdateAdminPricingSchema.parse(payload);
  const { data } = await api.put<AdminPricingConfig>(
    API_ROUTES.admin.pricingConfig,
    validatedPayload,
  );
  return data;
};

const fetchAdminCtsFormConfig = async (): Promise<AdminCtsFormConfig> => {
  const { data } = await api.get<AdminCtsFormConfig>(API_ROUTES.admin.ctsFormConfig);
  return data;
};

const updateCtsFormCountry = async (
  countryCode: string,
  payload: UpdateCtsFormCountryPayload,
): Promise<CtsDocument[]> => {
  const { data } = await api.put<CtsDocument[]>(
    API_ROUTES.admin.ctsFormCountry(countryCode),
    payload,
  );
  return data;
};

// ─── Query Options ────────────────────────────────────────────────────────────

export const getAdminCountryConfigQueryOptions = () =>
  queryOptions({
    queryKey: QUERY_KEYS.admin.countryConfig,
    queryFn: fetchAdminCountryConfig,
  });

export const getAdminServicesConfigQueryOptions = () =>
  queryOptions({
    queryKey: QUERY_KEYS.admin.servicesConfig,
    queryFn: fetchAdminServicesConfig,
  });

export const getAdminPricingConfigQueryOptions = () =>
  queryOptions({
    queryKey: QUERY_KEYS.admin.pricingConfig,
    queryFn: fetchAdminPricingConfig,
  });

export const getAdminCtsFormConfigQueryOptions = () =>
  queryOptions({
    queryKey: QUERY_KEYS.admin.ctsFormConfig,
    queryFn: fetchAdminCtsFormConfig,
  });

// ─── Hooks ────────────────────────────────────────────────────────────────────

export const useAdminCountryConfig = () => useQuery(getAdminCountryConfigQueryOptions());

export const useUpdateAdminCountryConfig = () => {
  const queryClient = useQueryClient();
  const invalidateNotifications = useInvalidateNotifications();
  return useMutation({
    mutationFn: (payload: UpdateAdminCountryConfigPayload) => updateAdminCountryConfig(payload),
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEYS.admin.countryConfig, data);
      invalidateNotifications();
      toaster.create({
        type: 'success',
        title: DICTIONARY.adminSettingsPage.toast.countryConfigSaved,
      });
    },
    onError: (error) => {
      toaster.create({
        type: 'error',
        title: DICTIONARY.adminSettingsPage.toast.countryConfigError,
        description: getApiErrorMessage(error),
      });
    },
  });
};

export const useAdminServicesConfig = () => useQuery(getAdminServicesConfigQueryOptions());

export const useUpdateAdminServicesConfig = () => {
  const queryClient = useQueryClient();
  const invalidateNotifications = useInvalidateNotifications();
  return useMutation({
    mutationFn: (payload: UpdateAdminServicesConfigPayload) => updateAdminServicesConfig(payload),
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEYS.admin.servicesConfig, data);
      // Platform visibility rides on the catalog rows the Services page reads, and the server
      // evicts its ten-minute catalog cache on this PUT. Without this the page keeps serving
      // the pre-save copy for the whole client staleTime.
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.services.catalogPrefix });
      invalidateNotifications();
      toaster.create({
        type: 'success',
        title: DICTIONARY.adminSettingsPage.toast.servicesConfigSaved,
      });
    },
    onError: (error) => {
      toaster.create({
        type: 'error',
        title: DICTIONARY.adminSettingsPage.toast.servicesConfigError,
        description: getApiErrorMessage(error),
      });
    },
  });
};

/**
 * Removes an offering from the platform catalog outright. This is not the visibility
 * switch: `PUT /admin/services-config` hides an offering, this drops the row, and the
 * catalog `PUT` cannot do it at all because the server treats that payload as an upsert.
 */
export const useDeleteCatalogItem = (entity: EntityType) => {
  const queryClient = useQueryClient();
  const invalidateNotifications = useInvalidateNotifications();
  const isService = entity === 'service';

  return useMutation({
    mutationFn: (id: string) => deleteCatalogItem(entity, id),
    onSuccess: (_data, id) => {
      // Deliberately an edit, not an invalidate. `GET /services/catalog` sits behind a
      // ten-minute server-side cache; the delete endpoints evict it now, but a refetch still
      // costs a round-trip to learn what this client already knows, and any API build that
      // predates that eviction fix would hand the deleted offering straight back.
      // Every wholesaler scope holds its own copy, hence the prefix match over all of them.
      queryClient.setQueriesData<ServiceFeatureCatalog>(
        { queryKey: QUERY_KEYS.services.catalogPrefix },
        (current) => (current == null ? current : withoutCatalogItem(current, entity, id)),
      );
      // Visibility lives on the offering row itself, so the delete took the config with it.
      // This read is uncached and reflects that immediately.
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.admin.servicesConfig });
      invalidateNotifications();
      toaster.create({
        type: 'success',
        title: isService
          ? DICTIONARY.adminSettingsPage.toast.serviceDeleted
          : DICTIONARY.adminSettingsPage.toast.featureDeleted,
      });
    },
    onError: (error) => {
      toaster.create({
        type: 'error',
        title: isService
          ? DICTIONARY.adminSettingsPage.toast.serviceDeleteError
          : DICTIONARY.adminSettingsPage.toast.featureDeleteError,
        description: getApiErrorMessage(error),
      });
    },
  });
};

export const useAdminPricingConfig = () => useQuery(getAdminPricingConfigQueryOptions());

export const useUpdateAdminPricingConfig = () => {
  const queryClient = useQueryClient();
  const invalidateNotifications = useInvalidateNotifications();
  return useMutation({
    mutationFn: (payload: UpdateAdminPricingPayload) => updateAdminPricingConfig(payload),
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEYS.admin.pricingConfig, data);
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.services.catalogPrefix });
      invalidateNotifications();
      toaster.create({
        type: 'success',
        title: DICTIONARY.adminSettingsPage.toast.pricingConfigSaved,
      });
    },
    onError: (error) => {
      toaster.create({
        type: 'error',
        title: DICTIONARY.adminSettingsPage.toast.pricingConfigError,
        description: getApiErrorMessage(error),
      });
    },
  });
};

export const useAdminCtsFormConfig = () => useQuery(getAdminCtsFormConfigQueryOptions());

export const useUpdateCtsFormCountry = () => {
  const queryClient = useQueryClient();
  const invalidateNotifications = useInvalidateNotifications();
  return useMutation({
    mutationFn: ({
      countryCode,
      payload,
    }: {
      countryCode: string;
      payload: UpdateCtsFormCountryPayload;
    }) => updateCtsFormCountry(countryCode, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: getAdminCtsFormConfigQueryOptions().queryKey,
      });
      invalidateNotifications();
      toaster.create({ type: 'success', title: DICTIONARY.adminSettingsPage.toast.ctsFormSaved });
    },
    onError: (error) => {
      toaster.create({
        type: 'error',
        title: DICTIONARY.adminSettingsPage.toast.ctsFormError,
        description: getApiErrorMessage(error),
      });
    },
  });
};
